import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';
import {VariableSizeList, ListChildComponentProps} from 'react-window';
import {AutoCompleteElementProps} from './AutoCompleteElement.type';
import SVGIcons from '@components/SVGIcon';

import cn from './Autocomplete.module.scss';
import './TextField.css';

const LISTBOX_PADDING = 8;

export const AutoCompleteElement = React.forwardRef(
  function AutoCompleteElement(
    {options, placeholder, onChange, value, ...props}: AutoCompleteElementProps,
    ref
  ) {
    return (
      <Autocomplete
        clearIcon={
          <span className={cn.arrow} data-testid='clear-icon'>
            <SVGIcons type={'close-svg'} />{' '}
          </span>
        }
        data-testid={'auto-complete'}
        popupIcon={
          <span className={cn.arrow}>
            <SVGIcons type={'downarrow-svg'} />{' '}
          </span>
        }
        className={`${value ? cn.active : cn.autoCompleteInput} ${
          props.className
        }`}
        ListboxComponent={ListboxComponent}
        options={options}
        disableListWrap
        sx={{height: '47px'}}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || 'City'}
            className={cn.textField}
          />
        )}
        renderOption={(props, option, state) =>
          [props, option, state.index] as React.ReactNode
        }
        ref={ref}
        value={value}
        onChange={(event, value: any) => onChange(value)}
        renderGroup={(params) => params as any}
        {...props}
      />
    );
  }
);

const renderRow = (props: ListChildComponentProps) => {
  const {data, index, style} = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: `${parseFloat(style.top as string) + LISTBOX_PADDING}px`,
    height: '40px',
  };

  return (
    <li
      {...dataSet[0]}
      key={index}
      style={inlineStyle}
      className={cn.listLabel}>
      {`${dataSet[1]}`}
    </li>
  );
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>(
  function OuterElementType(props, ref) {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  }
);

const useResetCache = (data: any) => {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

// Adapter for react-window
export const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const {children, ...other} = props;
  const itemData: React.ReactElement[] = [];
  (children as React.ReactElement[]).forEach(
    (item: React.ReactElement & {children?: React.ReactElement[]}) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactElement) => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref} className={cn.variableListWrapper}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='ul'
          itemSize={(index: number) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}>
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
